/**
 * ManageEpisodeModal — unified modal for managing any Episode of Care.
 *
 * Works for all episode types:
 *  • Couples / Family / Group therapy — full member management, primary patient selection,
 *    inline discharge form per member, guarantor with related-person picker, add-new-related-person form.
 *  • Individual / Medication Management — same member card with remove functionality;
 *    primary patient designation is fixed (no radio button).
 *
 * The episode is considered "all members inactive" once every member has been removed.
 * When that state is detected the modal shows a date-stamped marker instead of the member list.
 *
 * When the primary clinical patient changes the modal prompts for:
 *  1. New diagnosis (required) — individual-specific but the episode treatment plan is
 *     relationship-centric and can remain consistent.
 *  2. Treatment plan action — keep existing or mark for update with notes.
 */

import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Modal,
  Paper,
  Radio,
  ScrollArea,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconAlertTriangleFilled,
  IconHistory,
  IconPlayerPlay,
  IconPlus,
  IconShield,
  IconUserMinus,
  IconUserPlus,
  IconUsers,
  IconX,
} from '@tabler/icons-react';
import { DiagnosisSelect } from '../../../components/DiagnosisSelect';
import { getDiagnosisDescription } from '../../../data/diagnosisCodes';
import { formatUsDateShort, toFhirDateOnlyLocal } from '@/lib/dateHelpers';
import { useCallback, useEffect, useState } from 'react';
import type { GroupParticipationEndMode } from '@/lib/groupTherapy';
import type { EpisodeOfCareStatus } from '../types';
import type { DischargeSummary, PrimaryPatientSwitch } from '../../clients/data';

/** Group Therapy caseload participation actions (PRD §10). */
export type GroupTherapyCaseloadAction = GroupParticipationEndMode | 'return';

// ─── Public types ──────────────────────────────────────────────────────────────

export interface GroupMember {
  id: string;
  name: string;
  relationship?: string;
  phone?: string;
}

/** A contact person related to a group member but not receiving care (e.g. a parent, guardian). */
export interface RelatedPerson {
  id: string;
  name: string;
  relationship?: string;
  phone?: string;
  email?: string;
  /** The group member this person is linked to. */
  linkedToMemberId: string;
  linkedToMemberName: string;
}

export interface NewRelatedPersonInput {
  linkedToMemberId: string;
  name: string;
  relationship: string;
  phone?: string;
  email?: string;
}

export interface EpisodeManageUpdates {
  primaryClinicalPatientId: string | null;
  /** ID of the guarantor when they are a known member or related person; null for manual entries. */
  guarantorId: string | null;
  guarantorName: string;
  guarantorRelationship: string;
  switchReason: string;
  treatmentPlanUpdated: boolean;
  /** ICD-10 code for the new primary patient's diagnosis (e.g. "F41.1"). */
  newDiagnosisCode?: string;
  /** Human-readable description for the new diagnosis (e.g. "Generalized Anxiety Disorder"). */
  newDiagnosisDescription?: string;
  /** Combined "CODE - Description" string, derived from the two fields above. */
  newDiagnosis?: string;
  /** true = keep existing plan; false = plan needs updating */
  keepTreatmentPlan?: boolean;
  /** Provider notes on what needs to change in the plan. */
  treatmentPlanNotes?: string;
}

export interface ManageEpisodeModalProps {
  opened: boolean;
  onClose: () => void;
  episodeName: string;
  /** Human-readable group type label (e.g. "Couples Therapy"). Omit for individual episodes. */
  groupType?: string;
  /**
   * All members in this episode. For individual episodes pass the single client as the
   * only entry so they get the same remove-member workflow. Pass an empty array only if
   * all members have already been removed (triggers the all-inactive marker).
   */
  members: GroupMember[];
  /** Fallback display name when `members` is empty (e.g. for legacy callers). */
  clientName?: string;
  primaryClinicalPatientId: string | null;
  /**
   * Whether the primary patient designation can be changed.
   * Set `true` for group episodes, `false` for individual / fixed-primary episodes.
   * Defaults to `true` when there are multiple initial members.
   */
  allowPrimaryPatientChange?: boolean;
  /** Pre-fill the diagnosis code field on primary switch (e.g. "F41.1"). */
  currentDiagnosisCode?: string;
  /** Pre-fill the diagnosis description field on primary switch (e.g. "Generalized Anxiety Disorder"). */
  currentDiagnosisDescription?: string;
  /**
   * Combined diagnosis string used as a fallback when the separate code/description props are
   * not provided. Parsed as "CODE - Description".
   */
  currentDiagnosis?: string;
  /** Guarantor ID when the guarantor is a known member or related person. */
  guarantorId?: string | null;
  guarantorName?: string;
  guarantorRelationship?: string;
  /** Related persons from all group members' linked accounts. */
  relatedPersons?: RelatedPerson[];
  primaryPatientHistory?: PrimaryPatientSwitch[];
  /**
   * ISO date string set by the parent when ALL members have previously been removed.
   * Renders an "All members inactive since: <date>" marker instead of the member list.
   */
  allInactiveSince?: string;
  /**
   * When true, replace generic remove/discharge with Group Therapy suspend / formal discharge /
   * pre-start withdraw / return actions (FHIR-backed via `onGroupTherapyAction`).
   */
  isGroupTherapy?: boolean;
  /** Current episode status — used to show Return when on hold. */
  episodeStatus?: EpisodeOfCareStatus;
  /** Cohort display name from EoC group-reference extension. */
  groupCohortName?: string;
  onSave: (updates: EpisodeManageUpdates) => void;
  onMemberRemove?: (memberId: string, dischargeSummary: DischargeSummary) => void;
  /**
   * Persist Group Therapy participation changes to FHIR.
   * Required when `isGroupTherapy` is true.
   */
  onGroupTherapyAction?: (
    action: GroupTherapyCaseloadAction,
    memberId: string,
    dischargeSummary?: DischargeSummary,
  ) => void | Promise<void>;
  /** Called when a new related person is added so the parent can persist it. */
  onAddRelatedPerson?: (person: NewRelatedPersonInput) => void;
}

// ─── Internal constants ────────────────────────────────────────────────────────

const EXTERNAL_KEY = '__external__';
const ADD_NEW_KEY = '__add_new__';

const DISCHARGE_REASONS = [
  { value: 'Treatment goals met', label: 'Treatment goals met' },
  { value: 'Client request', label: 'Client request' },
  { value: 'Transferred to another provider', label: 'Transferred to another provider' },
  { value: 'Non-compliance', label: 'Non-compliance / no-show' },
  { value: 'Moved out of area', label: 'Moved out of area' },
  { value: 'Insurance change', label: 'Insurance change' },
  { value: 'Left group therapy', label: 'Left group / voluntary withdrawal' },
  { value: 'Other', label: 'Other' },
];

// ─── Helper ────────────────────────────────────────────────────────────────────

function resolveGuarantorKey(
  membersList: GroupMember[],
  relatedPersonsList: RelatedPerson[],
  gId?: string | null,
  gName?: string,
): string {
  if (gId) {
    if (membersList.some((m) => m.id === gId)) return gId;
    if (relatedPersonsList.some((rp) => rp.id === gId)) return `rp:${gId}`;
  }
  if (gName) {
    const byMember = membersList.find((m) => m.name === gName);
    if (byMember) return byMember.id;
    const byRp = relatedPersonsList.find((rp) => rp.name === gName);
    if (byRp) return `rp:${byRp.id}`;
  }
  return membersList[0]?.id ?? EXTERNAL_KEY;
}

/**
 * Date-only display for both calendar dates (YYYY-MM-DD) and instants
 * (changedAt): instants are converted to the browser-local calendar day.
 * Intentionally date-only per plan; not formatUsDateTime.
 */
function formatLocalDate(iso: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso.trim())) {
    return formatUsDateShort(iso.trim());
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return formatUsDateShort(d);
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function ManageEpisodeModal({
  opened,
  onClose,
  episodeName,
  groupType,
  members: initialMembers,
  clientName,
  primaryClinicalPatientId: initialPrimaryId,
  allowPrimaryPatientChange,
  currentDiagnosisCode: initialDiagnosisCode,
  currentDiagnosisDescription: initialDiagnosisDescription,
  currentDiagnosis,
  guarantorId: initialGuarantorId,
  guarantorName: initialGuarantorName = '',
  guarantorRelationship: initialGuarantorRelationship = '',
  relatedPersons: initialRelatedPersons = [],
  primaryPatientHistory,
  allInactiveSince,
  isGroupTherapy = false,
  episodeStatus,
  groupCohortName,
  onSave,
  onMemberRemove,
  onGroupTherapyAction,
  onAddRelatedPerson,
}: ManageEpisodeModalProps): JSX.Element {
  // A group episode is one where the primary patient can change (couples/family).
  // Group Therapy cohort EoCs are typically single-patient rows — do not force primary-change UX.
  const isGroupEpisode = allowPrimaryPatientChange ?? (!isGroupTherapy && initialMembers.length > 1);
  const canChangePrimary = isGroupEpisode && !isGroupTherapy;
  const isOnHold = episodeStatus === 'on-hold';

  // ── Core state ───────────────────────────────────────────────────────
  const [members, setMembers] = useState<GroupMember[]>(initialMembers);
  const [primaryId, setPrimaryId] = useState<string | null>(initialPrimaryId);
  const [sessionRelatedPersons, setSessionRelatedPersons] = useState<RelatedPerson[]>([]);

  const allRelatedPersons = [...initialRelatedPersons, ...sessionRelatedPersons];

  // ── Guarantor state ──────────────────────────────────────────────────
  const [guarantorKey, setGuarantorKey] = useState<string | null>(() =>
    resolveGuarantorKey(initialMembers, initialRelatedPersons, initialGuarantorId, initialGuarantorName),
  );
  const [extGuarantorName, setExtGuarantorName] = useState(() =>
    resolveGuarantorKey(initialMembers, initialRelatedPersons, initialGuarantorId, initialGuarantorName) ===
    EXTERNAL_KEY
      ? initialGuarantorName
      : '',
  );
  const [extGuarantorRelationship, setExtGuarantorRelationship] = useState(() =>
    resolveGuarantorKey(initialMembers, initialRelatedPersons, initialGuarantorId, initialGuarantorName) ===
    EXTERNAL_KEY
      ? initialGuarantorRelationship
      : '',
  );

  // ── Primary patient change state ─────────────────────────────────────
  const [switchReason, setSwitchReason] = useState('');
  // Parse fallback combined string "CODE - Description" the same way TreatmentPlanPage does
  const [newDiagnosisCode, setNewDiagnosisCode] = useState(
    initialDiagnosisCode ?? currentDiagnosis?.split(' - ')[0] ?? '',
  );
  const [newDiagnosisDescription, setNewDiagnosisDescription] = useState(
    initialDiagnosisDescription ?? currentDiagnosis?.split(' - ').slice(1).join(' - ') ?? '',
  );
  const [treatmentPlanAction, setTreatmentPlanAction] = useState<'keep' | 'update'>('keep');
  const [treatmentPlanNotes, setTreatmentPlanNotes] = useState('');
  const [treatmentPlanUpdated, setTreatmentPlanUpdated] = useState(false);

  const clinicalPatientChanging = canChangePrimary && primaryId !== initialPrimaryId;

  // ── Add new related person sub-form state ────────────────────────────
  const [addingRelatedPerson, setAddingRelatedPerson] = useState(false);
  const [newRpLinkedTo, setNewRpLinkedTo] = useState<string | null>(null);
  const [newRpName, setNewRpName] = useState('');
  const [newRpRelationship, setNewRpRelationship] = useState('');
  const [newRpPhone, setNewRpPhone] = useState('');
  const [newRpEmail, setNewRpEmail] = useState('');

  // ── Member removal / Group Therapy action state ──────────────────────
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [dischargeReason, setDischargeReason] = useState('');
  const [dischargeCategory, setDischargeCategory] = useState<string | null>(null);
  const [groupActionMode, setGroupActionMode] = useState<GroupParticipationEndMode | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  // Track when all members become inactive within this modal session
  const [sessionAllInactiveSince, setSessionAllInactiveSince] = useState<string | null>(null);

  const isRemoving = removingMemberId !== null;

  // The date to display in the all-inactive marker (prefer prop from parent, fall back to session)
  const inactiveSince = allInactiveSince ?? sessionAllInactiveSince;

  // ── Reset on open ────────────────────────────────────────────────────
  const resetForm = useCallback(() => {
    setMembers(initialMembers);
    setPrimaryId(initialPrimaryId);
    setSessionRelatedPersons([]);

    const key = resolveGuarantorKey(
      initialMembers,
      initialRelatedPersons,
      initialGuarantorId,
      initialGuarantorName,
    );
    setGuarantorKey(key);
    setExtGuarantorName(key === EXTERNAL_KEY ? initialGuarantorName : '');
    setExtGuarantorRelationship(key === EXTERNAL_KEY ? initialGuarantorRelationship : '');

    setSwitchReason('');
    setNewDiagnosisCode(initialDiagnosisCode ?? currentDiagnosis?.split(' - ')[0] ?? '');
    setNewDiagnosisDescription(
      initialDiagnosisDescription ?? currentDiagnosis?.split(' - ').slice(1).join(' - ') ?? '',
    );
    setTreatmentPlanAction('keep');
    setTreatmentPlanNotes('');
    setTreatmentPlanUpdated(false);

    setAddingRelatedPerson(false);
    setNewRpLinkedTo(null);
    setNewRpName('');
    setNewRpRelationship('');
    setNewRpPhone('');
    setNewRpEmail('');

    setRemovingMemberId(null);
    setDischargeReason('');
    setDischargeCategory(null);
    setGroupActionMode(null);
    setActionBusy(false);
    setSessionAllInactiveSince(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialMembers,
    initialPrimaryId,
    initialRelatedPersons,
    initialGuarantorId,
    initialGuarantorName,
    initialGuarantorRelationship,
    initialDiagnosisCode,
    initialDiagnosisDescription,
    currentDiagnosis,
  ]);

  useEffect(() => {
    if (opened) resetForm();
  }, [opened, resetForm]);

  // ── Add related person handlers ──────────────────────────────────────
  const cancelAddRelatedPerson = () => {
    setAddingRelatedPerson(false);
    setNewRpLinkedTo(null);
    setNewRpName('');
    setNewRpRelationship('');
    setNewRpPhone('');
    setNewRpEmail('');
  };

  const handleAddRelatedPersonConfirm = () => {
    if (!newRpName.trim()) return;

    const linkedMemberId =
      newRpLinkedTo ?? members[0]?.id ?? (clientName ? 'self' : null);
    if (!linkedMemberId) return;

    const linkedMemberName =
      members.find((m) => m.id === linkedMemberId)?.name ??
      (linkedMemberId === 'self' ? (clientName ?? 'Client') : linkedMemberId);

    const newRp: RelatedPerson = {
      id: `rp-${Date.now()}`,
      name: newRpName.trim(),
      relationship: newRpRelationship.trim() || undefined,
      phone: newRpPhone.trim() || undefined,
      email: newRpEmail.trim() || undefined,
      linkedToMemberId: linkedMemberId,
      linkedToMemberName: linkedMemberName,
    };

    setSessionRelatedPersons((prev) => [...prev, newRp]);
    setGuarantorKey(`rp:${newRp.id}`);

    onAddRelatedPerson?.({
      linkedToMemberId: linkedMemberId,
      name: newRp.name,
      relationship: newRpRelationship.trim(),
      phone: newRpPhone.trim() || undefined,
      email: newRpEmail.trim() || undefined,
    });

    cancelAddRelatedPerson();
  };

  // ── Remove member / Group Therapy action handlers ────────────────────
  const cancelRemoval = () => {
    setRemovingMemberId(null);
    setDischargeReason('');
    setDischargeCategory(null);
    setGroupActionMode(null);
  };

  const buildDischargeSummary = (): DischargeSummary => ({
    id: `DS-${Date.now()}`,
    episodeId: '',
    dischargeDate: toFhirDateOnlyLocal(new Date()),
    signedBy: 'Current Provider',
    signedAt: new Date().toISOString(),
    reasonForDischarge: dischargeCategory ?? 'Other',
    summaryOfCare: dischargeReason.trim(),
    recommendations: '',
  });

  const applyLocalMemberRemoval = (memberId: string) => {
    const remaining = members.filter((m) => m.id !== memberId);
    setMembers(remaining);
    if (primaryId === memberId) setPrimaryId(remaining[0]?.id ?? null);
    if (guarantorKey === memberId) {
      setGuarantorKey(remaining[0]?.id ?? EXTERNAL_KEY);
    }
    if (remaining.length === 0) {
      setSessionAllInactiveSince(toFhirDateOnlyLocal(new Date()));
    }
  };

  const handleConfirmRemove = () => {
    if (!removingMemberId || !dischargeReason.trim()) return;

    const summary = buildDischargeSummary();
    onMemberRemove?.(removingMemberId, summary);
    applyLocalMemberRemoval(removingMemberId);
    cancelRemoval();
  };

  const handleConfirmGroupTherapyAction = async () => {
    if (!removingMemberId || !groupActionMode || !onGroupTherapyAction) return;
    if (groupActionMode !== 'suspend' && !dischargeReason.trim()) return;

    setActionBusy(true);
    try {
      const summary =
        groupActionMode === 'suspend' ? undefined : buildDischargeSummary();
      await onGroupTherapyAction(groupActionMode, removingMemberId, summary);
      if (groupActionMode !== 'suspend') {
        applyLocalMemberRemoval(removingMemberId);
      }
      cancelRemoval();
    } finally {
      setActionBusy(false);
    }
  };

  const handleReturnToGroup = async (memberId: string) => {
    if (!onGroupTherapyAction) return;
    setActionBusy(true);
    try {
      await onGroupTherapyAction('return', memberId);
    } finally {
      setActionBusy(false);
    }
  };

  const startGroupTherapyAction = (memberId: string, mode: GroupParticipationEndMode) => {
    setRemovingMemberId(memberId);
    setGroupActionMode(mode);
    setDischargeReason('');
    setDischargeCategory(mode === 'pre-start-withdraw' ? 'Left group therapy' : null);
  };

  // ── Save handler ─────────────────────────────────────────────────────
  const handleSave = () => {
    let finalGuarantorId: string | null = null;
    let finalName = '';
    let finalRelationship = '';

    if (guarantorKey && guarantorKey !== EXTERNAL_KEY && guarantorKey !== ADD_NEW_KEY) {
      if (guarantorKey.startsWith('rp:')) {
        const rpId = guarantorKey.slice(3);
        const rp = allRelatedPersons.find((r) => r.id === rpId);
        finalGuarantorId = rpId;
        finalName = rp?.name ?? '';
        finalRelationship = rp?.relationship ?? '';
      } else {
        const member = members.find((m) => m.id === guarantorKey);
        finalGuarantorId = guarantorKey;
        finalName = member?.name ?? '';
        finalRelationship = member?.relationship ?? 'Self';
      }
    } else if (guarantorKey === EXTERNAL_KEY) {
      finalName = extGuarantorName.trim();
      finalRelationship = extGuarantorRelationship.trim();
    }

    const codeOut = clinicalPatientChanging ? newDiagnosisCode.trim() : undefined;
    const descOut = clinicalPatientChanging ? newDiagnosisDescription.trim() : undefined;
    const combinedDiagnosis =
      codeOut || descOut
        ? [codeOut, descOut].filter(Boolean).join(' - ')
        : undefined;

    onSave({
      primaryClinicalPatientId: primaryId,
      guarantorId: finalGuarantorId,
      guarantorName: finalName,
      guarantorRelationship: finalRelationship,
      switchReason,
      treatmentPlanUpdated,
      newDiagnosisCode: codeOut || undefined,
      newDiagnosisDescription: descOut || undefined,
      newDiagnosis: combinedDiagnosis,
      keepTreatmentPlan: clinicalPatientChanging ? treatmentPlanAction === 'keep' : undefined,
      treatmentPlanNotes:
        clinicalPatientChanging && treatmentPlanAction === 'update'
          ? treatmentPlanNotes.trim() || undefined
          : undefined,
    });
    onClose();
  };

  // ── Derived / helpers ────────────────────────────────────────────────

  const attachToOptions =
    members.length > 0
      ? members.map((m) => ({ value: m.id, label: m.name }))
      : clientName
        ? [{ value: 'self', label: clientName }]
        : [];

  const guarantorOptions: { group: string; items: { value: string; label: string }[] }[] = [
    ...(members.length > 0
      ? [{ group: isGroupEpisode ? 'Group Members' : 'Client', items: members.map((m) => ({ value: m.id, label: m.name })) }]
      : []),
    ...(allRelatedPersons.length > 0
      ? [
          {
            group: 'Related Persons',
            items: allRelatedPersons.map((rp) => ({
              value: `rp:${rp.id}`,
              label: `${rp.name} — ${rp.linkedToMemberName}'s ${rp.relationship ?? 'contact'}`,
            })),
          },
        ]
      : []),
    {
      group: 'Other',
      items: [
        { value: EXTERNAL_KEY, label: 'Enter manually...' },
        { value: ADD_NEW_KEY, label: '+ Add new related person' },
      ],
    },
  ];

  const displayGuarantorKey = addingRelatedPerson ? null : guarantorKey;

  const isSaveDisabled =
    isRemoving ||
    actionBusy ||
    addingRelatedPerson ||
    (clinicalPatientChanging && !switchReason.trim()) ||
    (clinicalPatientChanging && !newDiagnosisCode.trim()) ||
    (clinicalPatientChanging && treatmentPlanAction === 'update' && !treatmentPlanNotes.trim());

  // ── Render helpers ───────────────────────────────────────────────────

  const groupActionTitle = (mode: GroupParticipationEndMode | null): string => {
    switch (mode) {
      case 'suspend':
        return 'Suspend (On Hold)';
      case 'formal-discharge':
        return 'Formal discharge';
      case 'pre-start-withdraw':
        return 'Pre-start withdraw';
      default:
        return 'End participation';
    }
  };

  /** Member card content shared between group (with radio) and individual (without radio). */
  const renderMemberCard = (member: GroupMember) => {
    const isBeingRemoved = removingMemberId === member.id;
    // Track the live primaryId (not initial) so the badge + block react to radio changes instantly
    const isPrimary = member.id === primaryId;

    // The current primary patient cannot be removed while other active members remain.
    // The user must reassign primary first using the radio buttons, then remove.
    const isBlockedPrimary = canChangePrimary && isPrimary && members.length > 1;
    const removeDisabled = isRemoving || actionBusy || addingRelatedPerson || isBlockedPrimary;
    const removeTooltip = isBlockedPrimary
      ? 'Reassign the primary patient first, then you can remove this member'
      : isGroupTherapy
        ? 'End group participation'
        : 'Remove from episode';

    return (
      <Box key={member.id}>
        <Paper
          withBorder
          p="sm"
          style={{
            borderColor: isBeingRemoved ? 'var(--mantine-color-red-4)' : undefined,
            backgroundColor: isBeingRemoved ? 'var(--mantine-color-red-0)' : undefined,
            transition: 'border-color 0.15s ease, background-color 0.15s ease',
          }}
        >
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
              {canChangePrimary && (
                <Radio
                  value={member.id}
                  disabled={isRemoving || actionBusy || !canChangePrimary}
                  aria-label={`Set ${member.name} as primary clinical patient`}
                />
              )}
              <div style={{ minWidth: 0 }}>
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" fw={500} truncate>
                    {member.name}
                  </Text>
                  {isPrimary && canChangePrimary && (
                    <Badge size="xs" color="blue" variant="light">
                      Primary
                    </Badge>
                  )}
                  {isGroupTherapy && isOnHold && (
                    <Badge size="xs" color="orange" variant="light">
                      On Hold
                    </Badge>
                  )}
                </Group>
                {member.relationship && (
                  <Text size="xs" c="gray.6">
                    {member.relationship}
                  </Text>
                )}
              </div>
            </Group>

            {isBeingRemoved ? (
              <Tooltip label="Cancel" withArrow>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="gray"
                  onClick={cancelRemoval}
                  disabled={actionBusy}
                  aria-label="Cancel action"
                >
                  <IconX size={14} />
                </ActionIcon>
              </Tooltip>
            ) : isGroupTherapy && isOnHold ? (
              <Button
                size="xs"
                variant="light"
                color="green"
                leftSection={<IconPlayerPlay size={14} />}
                loading={actionBusy}
                disabled={actionBusy || addingRelatedPerson}
                onClick={() => void handleReturnToGroup(member.id)}
              >
                Return to group
              </Button>
            ) : (
              // Wrap in a Box so the Tooltip still activates when the button is disabled
              <Tooltip label={removeTooltip} withArrow>
                <Box component="span" style={{ display: 'inline-block' }}>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color={isBlockedPrimary ? 'gray' : 'red'}
                    disabled={removeDisabled}
                    style={removeDisabled ? { pointerEvents: 'none' } : undefined}
                    onClick={() => {
                      if (isGroupTherapy) {
                        startGroupTherapyAction(member.id, 'suspend');
                        return;
                      }
                      setRemovingMemberId(member.id);
                      setDischargeReason('');
                      setDischargeCategory(null);
                      setGroupActionMode(null);
                    }}
                    aria-label={
                      isGroupTherapy
                        ? `End group participation for ${member.name}`
                        : `Remove ${member.name} from episode`
                    }
                  >
                    <IconUserMinus size={14} />
                  </ActionIcon>
                </Box>
              </Tooltip>
            )}
          </Group>
        </Paper>

        {/* Inline Group Therapy participation actions */}
        {isBeingRemoved && isGroupTherapy && groupActionMode && (
          <Paper
            p="md"
            style={{
              border: '1px solid var(--mantine-color-orange-3)',
              borderTop: 'none',
              borderRadius: '0 0 var(--mantine-radius-sm) var(--mantine-radius-sm)',
              backgroundColor: 'var(--mantine-color-orange-0)',
            }}
          >
            <Stack gap="sm">
              <Text size="sm" fw={600}>
                {groupActionTitle(groupActionMode)} — {member.name}
              </Text>
              <Text size="xs" c="gray.6">
                Choose how to end this client&apos;s Group Therapy participation. Suspend keeps the
                episode on hold so they can return later.
              </Text>
              <Group gap="xs">
                <Button
                  size="xs"
                  variant={groupActionMode === 'suspend' ? 'filled' : 'light'}
                  color="orange"
                  onClick={() => setGroupActionMode('suspend')}
                  disabled={actionBusy}
                >
                  Suspend (On Hold)
                </Button>
                <Button
                  size="xs"
                  variant={groupActionMode === 'formal-discharge' ? 'filled' : 'light'}
                  color="red"
                  onClick={() => setGroupActionMode('formal-discharge')}
                  disabled={actionBusy}
                >
                  Formal discharge
                </Button>
                <Button
                  size="xs"
                  variant={groupActionMode === 'pre-start-withdraw' ? 'filled' : 'light'}
                  color="gray"
                  onClick={() => {
                    setGroupActionMode('pre-start-withdraw');
                    setDischargeCategory((prev) => prev ?? 'Left group therapy');
                  }}
                  disabled={actionBusy}
                >
                  Pre-start withdraw
                </Button>
              </Group>

              {groupActionMode === 'suspend' ? (
                <Alert color="orange" variant="light" py="xs">
                  Places the episode on hold and removes the client from the active Group roster.
                  CareTeam stays active so they can return later.
                </Alert>
              ) : (
                <>
                  <Select
                    label="Reason"
                    placeholder="Select a reason..."
                    data={DISCHARGE_REASONS}
                    value={dischargeCategory}
                    onChange={setDischargeCategory}
                    size="sm"
                    disabled={actionBusy}
                  />
                  <Textarea
                    label={
                      groupActionMode === 'pre-start-withdraw'
                        ? 'Withdrawal notes'
                        : 'Discharge summary'
                    }
                    placeholder="Document the reason and any follow-up recommendations..."
                    value={dischargeReason}
                    onChange={(e) => setDischargeReason(e.currentTarget.value)}
                    minRows={3}
                    autosize
                    maxRows={8}
                    required
                    size="sm"
                    disabled={actionBusy}
                  />
                </>
              )}

              <Group justify="flex-end" gap="xs">
                <Button size="xs" variant="default" onClick={cancelRemoval} disabled={actionBusy}>
                  Cancel
                </Button>
                <Button
                  size="xs"
                  color={groupActionMode === 'suspend' ? 'orange' : 'red'}
                  leftSection={<IconUserMinus size={14} />}
                  loading={actionBusy}
                  disabled={groupActionMode !== 'suspend' && !dischargeReason.trim()}
                  onClick={() => void handleConfirmGroupTherapyAction()}
                >
                  Confirm {groupActionTitle(groupActionMode)}
                </Button>
              </Group>
            </Stack>
          </Paper>
        )}

        {/* Inline discharge form (couples / family / individual) */}
        {isBeingRemoved && !isGroupTherapy && (
          <Paper
            p="md"
            style={{
              border: '1px solid var(--mantine-color-red-3)',
              borderTop: 'none',
              borderRadius: '0 0 var(--mantine-radius-sm) var(--mantine-radius-sm)',
              backgroundColor: 'var(--mantine-color-red-0)',
            }}
          >
            <Stack gap="sm">
              <Alert
                icon={<IconAlertTriangleFilled size={14} />}
                color="red"
                variant="light"
                title={`Removing ${member.name} from this episode`}
                py="xs"
              >
                A discharge summary is required before removing a member from the episode.
              </Alert>

              <Select
                label="Reason for discharge"
                placeholder="Select a reason..."
                data={DISCHARGE_REASONS}
                value={dischargeCategory}
                onChange={setDischargeCategory}
                size="sm"
              />

              <Textarea
                label="Discharge summary"
                placeholder="Summarize the course of treatment, progress made, and any follow-up recommendations..."
                value={dischargeReason}
                onChange={(e) => setDischargeReason(e.currentTarget.value)}
                minRows={4}
                autosize
                maxRows={10}
                required
                size="sm"
              />

              <Group justify="flex-end" gap="xs">
                <Button size="xs" variant="default" onClick={cancelRemoval}>
                  Cancel
                </Button>
                <Button
                  size="xs"
                  color="red"
                  leftSection={<IconUserMinus size={14} />}
                  disabled={!dischargeReason.trim()}
                  onClick={handleConfirmRemove}
                >
                  Confirm Removal
                </Button>
              </Group>
            </Stack>
          </Paper>
        )}
      </Box>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconUsers size={18} />
          <Text fw={600}>Manage Episode</Text>
          {isGroupTherapy && (
            <Badge variant="light" color="violet" size="sm">
              Group Therapy
            </Badge>
          )}
          {groupType && !isGroupTherapy && (
            <Badge variant="light" color="blue" size="sm">
              {groupType}
            </Badge>
          )}
          {!isGroupEpisode && !isGroupTherapy && (
            <Badge variant="light" color="gray" size="sm">
              Individual
            </Badge>
          )}
        </Group>
      }
      size="lg"
      centered
    >
      <ScrollArea.Autosize mah={700}>
        <Stack gap="md" pr={4}>
          <Text size="sm" c="gray.6">
            Episode:{' '}
            <Text span fw={500} c="gray.8">
              {episodeName}
            </Text>
            {groupCohortName ? (
              <>
                {' · '}
                Cohort:{' '}
                <Text span fw={500} c="gray.8">
                  {groupCohortName}
                </Text>
              </>
            ) : null}
          </Text>

          {/* ── Members ─────────────────────────────────────────────── */}
          <Divider
            label={isGroupTherapy ? 'Group Therapy Client' : isGroupEpisode ? 'Group Members' : 'Primary Patient'}
            labelPosition="left"
          />

          {members.length === 0 ? (
            /* All-inactive marker */
            <Alert
              icon={<IconUsers size={14} />}
              color="gray"
              variant="light"
              title="All members inactive"
            >
              All members have been discharged from this episode.
              {inactiveSince && (
                <Text size="xs" c="gray.5" mt={4}>
                  Inactive since: {formatLocalDate(inactiveSince)}
                </Text>
              )}
            </Alert>
          ) : (
            <>
              {isGroupTherapy ? (
                <Text size="xs" c="gray.6">
                  {isOnHold
                    ? 'This client is on hold. Return them to the group to reactivate membership and the episode.'
                    : 'Suspend places the episode on hold. Formal discharge finishes the episode. Pre-start withdraw cancels before the group begins.'}
                </Text>
              ) : isGroupEpisode ? (
                <Text size="xs" c="gray.6">
                  {canChangePrimary
                    ? 'Use the radio buttons to set the primary clinical patient. Remove a member to discharge them from this episode.'
                    : 'Members of this episode.'}
                </Text>
              ) : null}

              {canChangePrimary ? (
                <Radio.Group
                  value={primaryId ?? ''}
                  onChange={setPrimaryId}
                  name="primary-clinical-patient"
                >
                  <Stack gap="sm">{members.map(renderMemberCard)}</Stack>
                </Radio.Group>
              ) : (
                <Stack gap="sm">{members.map(renderMemberCard)}</Stack>
              )}

              {isGroupEpisode && !isGroupTherapy && (
                <Button
                  variant="subtle"
                  size="xs"
                  leftSection={<IconPlus size={14} />}
                  color="gray"
                  style={{ alignSelf: 'flex-start' }}
                  disabled
                  title="Add member (coming soon)"
                >
                  Add member
                </Button>
              )}
            </>
          )}

          {/* ── Primary Patient Change prompts ───────────────────────── */}
          {clinicalPatientChanging && (
            <>
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="orange"
                variant="light"
                title="Mid-episode primary patient change"
              >
                The treatment plan is relationship-centric and can remain consistent, but the{' '}
                <strong>diagnosis must be updated</strong> to reflect the new primary patient.
                This change creates a permanent audit record.
              </Alert>

              <DiagnosisSelect
                code={newDiagnosisCode}
                description={newDiagnosisDescription}
                onCodeChange={(code) => {
                  setNewDiagnosisCode(code);
                  const autoDescription = code ? getDiagnosisDescription(code) : '';
                  if (autoDescription) {
                    setNewDiagnosisDescription(autoDescription);
                  } else if (!code) {
                    setNewDiagnosisDescription('');
                  }
                }}
                onDescriptionChange={setNewDiagnosisDescription}
                required
              />

              <Textarea
                label="Reason for change"
                description="Document why the primary clinical patient designation is changing"
                placeholder="e.g. Insurance change — previous member's coverage lapsed"
                value={switchReason}
                onChange={(e) => setSwitchReason(e.currentTarget.value)}
                required
                minRows={2}
              />

              <Divider label="Treatment plan" labelPosition="left" />

              <Text size="xs" c="gray.6">
                The treatment plan ideally treats the relationship and remains consistent.
                Only update it if the therapeutic goals need to change for the new primary patient.
              </Text>

              <Radio.Group
                value={treatmentPlanAction}
                onChange={(v) => setTreatmentPlanAction(v as 'keep' | 'update')}
                name="treatment-plan-action"
              >
                <Stack gap="xs">
                  <Radio
                    value="keep"
                    label="Keep existing treatment plan — relationship goals remain applicable"
                  />
                  <Radio
                    value="update"
                    label="Update treatment plan — goals need to change for new primary patient"
                  />
                </Stack>
              </Radio.Group>

              {treatmentPlanAction === 'update' && (
                <Textarea
                  label="Treatment plan update notes"
                  description="Describe what changes are needed. Use these notes when updating the full treatment plan."
                  placeholder="e.g. Replace individual anxiety goals with relationship-centered communication goals..."
                  value={treatmentPlanNotes}
                  onChange={(e) => setTreatmentPlanNotes(e.currentTarget.value)}
                  required
                  minRows={3}
                />
              )}

              <Checkbox
                label="Treatment plan has been updated to reflect the new primary patient"
                checked={treatmentPlanUpdated}
                onChange={(e) => setTreatmentPlanUpdated(e.currentTarget.checked)}
              />
            </>
          )}

          {/* ── Guarantor ────────────────────────────────────────────── */}
          <Divider
            label={
              <Group gap={4}>
                <IconShield size={12} />
                <Text size="xs">Financially responsible party (Guarantor)</Text>
              </Group>
            }
            labelPosition="left"
          />

          <Text size="xs" c="gray.6">
            The guarantor is legally and financially responsible for any balance not covered by
            insurance. This may be a group member, an existing related person (e.g. a parent or
            guardian), or an external party.
          </Text>

          <Select
            label="Guarantor"
            data={guarantorOptions}
            value={displayGuarantorKey}
            onChange={(value) => {
              if (value === ADD_NEW_KEY) {
                setAddingRelatedPerson(true);
                setNewRpLinkedTo(members[0]?.id ?? (clientName ? 'self' : null));
                return;
              }
              setAddingRelatedPerson(false);
              setGuarantorKey(value);
            }}
            leftSection={<IconShield size={14} />}
            disabled={isRemoving || actionBusy}
            placeholder="Select guarantor..."
            clearable={false}
          />

          {/* Manual entry */}
          {guarantorKey === EXTERNAL_KEY && !addingRelatedPerson && (
            <Stack gap="xs">
              <TextInput
                label="Guarantor name"
                placeholder="Full name"
                value={extGuarantorName}
                onChange={(e) => setExtGuarantorName(e.currentTarget.value)}
                disabled={isRemoving || actionBusy}
              />
              <TextInput
                label="Relationship to primary patient"
                placeholder="e.g. Parent, Guardian, Spouse"
                value={extGuarantorRelationship}
                onChange={(e) => setExtGuarantorRelationship(e.currentTarget.value)}
                disabled={isRemoving || actionBusy}
              />
            </Stack>
          )}

          {/* Add new related person sub-form */}
          {addingRelatedPerson && (
            <Paper
              withBorder
              p="md"
              style={{
                borderColor: 'var(--mantine-color-blue-3)',
                backgroundColor: 'var(--mantine-color-blue-0)',
              }}
            >
              <Stack gap="sm">
                <Group gap="xs">
                  <IconUserPlus
                    size={16}
                    style={{ color: 'var(--mantine-color-blue-6)' }}
                  />
                  <Text size="sm" fw={600} c="blue.7">
                    Add Related Person
                  </Text>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="gray"
                    onClick={cancelAddRelatedPerson}
                    ml="auto"
                    aria-label="Cancel"
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </Group>

                <Text size="xs" c="gray.6">
                  A related person is someone not receiving care who is financially responsible
                  or has a relevant relationship with a client in this episode (e.g. a parent,
                  guardian, or emergency contact).
                </Text>

                {attachToOptions.length > 1 && (
                  <Select
                    label="Linked to"
                    description="Which group member is this person related to?"
                    data={attachToOptions}
                    value={newRpLinkedTo}
                    onChange={setNewRpLinkedTo}
                    required
                    size="sm"
                  />
                )}

                <Group grow gap="xs">
                  <TextInput
                    label="Full name"
                    placeholder="e.g. Robert Rivera"
                    value={newRpName}
                    onChange={(e) => setNewRpName(e.currentTarget.value)}
                    required
                    size="sm"
                  />
                  <TextInput
                    label="Relationship"
                    placeholder="e.g. Parent, Guardian"
                    value={newRpRelationship}
                    onChange={(e) => setNewRpRelationship(e.currentTarget.value)}
                    size="sm"
                  />
                </Group>

                <Group grow gap="xs">
                  <TextInput
                    label="Phone"
                    placeholder="(555) 000-0000"
                    value={newRpPhone}
                    onChange={(e) => setNewRpPhone(e.currentTarget.value)}
                    size="sm"
                  />
                  <TextInput
                    label="Email (optional)"
                    placeholder="email@example.com"
                    value={newRpEmail}
                    onChange={(e) => setNewRpEmail(e.currentTarget.value)}
                    size="sm"
                  />
                </Group>

                <Group justify="flex-end" gap="xs">
                  <Button size="xs" variant="default" onClick={cancelAddRelatedPerson}>
                    Cancel
                  </Button>
                  <Button
                    size="xs"
                    leftSection={<IconUserPlus size={14} />}
                    disabled={
                      !newRpName.trim() ||
                      (attachToOptions.length > 1 && !newRpLinkedTo)
                    }
                    onClick={handleAddRelatedPersonConfirm}
                  >
                    Add Related Person
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )}

          {/* ── Primary Patient Change History ───────────────────────── */}
          {primaryPatientHistory && primaryPatientHistory.length > 0 && (
            <>
              <Divider
                label={
                  <Group gap={4}>
                    <IconHistory size={12} />
                    <Text size="xs">Primary patient change history</Text>
                  </Group>
                }
                labelPosition="left"
              />
              <Stack gap="xs">
                {primaryPatientHistory.map((entry, i) => (
                  <Box
                    key={i}
                    p="xs"
                    style={{
                      background: 'var(--mantine-color-gray-0)',
                      borderRadius: 'var(--mantine-radius-sm)',
                      borderLeft: '3px solid var(--mantine-color-orange-4)',
                    }}
                  >
                    <Text size="xs" fw={500} c="gray.8">
                      {entry.previousPatient.name} → {entry.newPatient.name}
                    </Text>
                    <Text size="xs" c="gray.6">
                      {formatLocalDate(entry.changedAt)}
                      {' · '}Changed by {entry.changedBy}
                    </Text>
                    <Text size="xs" c="gray.6" mt={2}>
                      Reason: {entry.reason}
                    </Text>
                    {entry.treatmentPlanUpdated && (
                      <Badge size="xs" color="green" variant="light" mt={4}>
                        Treatment plan updated
                      </Badge>
                    )}
                  </Box>
                ))}
              </Stack>
            </>
          )}

          <Group justify="flex-end" mt="xs">
            <Button
              variant="default"
              onClick={onClose}
              disabled={isRemoving || actionBusy || addingRelatedPerson}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaveDisabled}>
              Save Changes
            </Button>
          </Group>
        </Stack>
      </ScrollArea.Autosize>
    </Modal>
  );
}
